import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const typeBadge = (ty) =>
  ty === "checkout"
    ? { bg: "#DBEAFE", fg: "#1D4ED8", label: "Check-out" }
    : { bg: "#F9F3E5", fg: "#B8860B", label: "Service" };

export default function InspectionsIndex() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [q, setQ] = useState("");
  const [property, setProperty] = useState("all");
  const [serviceType, setServiceType] = useState("all");
  const isGroupView = user?.role === "owner" || user?.role === "management";

  useEffect(() => {
    Promise.all([
      api.get("/inspections", { params: { limit: 1000 } }),
      api.get("/config/properties"),
    ]).then(([a, b]) => {
      setRows(a.data);
      setProperties(b.data.properties);
    });
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (property !== "all" && r.property_id !== property) return false;
      if (serviceType !== "all" && r.service_type !== serviceType) return false;
      if (q) {
        const s = q.toLowerCase();
        if (![r.room_number, r.supervisor_name, r.guest_name, r.property_name].join(" ").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [rows, property, serviceType, q]);

  return (
    <Layout title="Cleaning Log" subtitle={isGroupView ? "All Properties · Every Room Cleaning" : "My Checklist Submissions"}>
      <div className="bg-white border border-[var(--border-warm)] rounded-lg overflow-hidden">
        <div className="p-5 border-b border-[var(--border-subtle)] bg-[var(--surface-elev)] flex flex-wrap gap-3 items-end justify-between">
          <div className="flex-1 min-w-[200px]">
            <label className="hotel-overline block mb-1">Search</label>
            <Input data-testid="log-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Room, guest, supervisor…" className="h-10 bg-white border-[var(--border-warm)]" />
          </div>
          {isGroupView && (
            <div>
              <label className="hotel-overline block mb-1">Property</label>
              <Select value={property} onValueChange={setProperty}>
                <SelectTrigger data-testid="log-filter-property" className="w-44 h-10 bg-white border-[var(--border-warm)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name.replace(" Hotel", "")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="hotel-overline block mb-1">Type</label>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger data-testid="log-filter-type" className="w-36 h-10 bg-white border-[var(--border-warm)]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="checkout">Check-out</SelectItem>
                <SelectItem value="service">Service</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[var(--surface-elev)]/60 hover:bg-[var(--surface-elev)]/60">
                <TableHead>Date & Time (24h)</TableHead>
                {isGroupView && <TableHead>Property</TableHead>}
                <TableHead>Room</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Guest</TableHead>
                <TableHead>Supervisor</TableHead>
                <TableHead>Shift</TableHead>
                <TableHead className="text-right">Report</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-[var(--muted)] py-8">No records yet.</TableCell></TableRow>
              )}
              {filtered.map((r) => {
                const tb = typeBadge(r.service_type);
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono-hotel text-xs">{fmtDateTime(r.created_at)}</TableCell>
                    {isGroupView && <TableCell className="text-[var(--ink-2)]">{r.property_name?.replace(" Hotel", "")}</TableCell>}
                    <TableCell className="font-mono-hotel font-semibold">{r.room_number}</TableCell>
                    <TableCell>
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: tb.bg, color: tb.fg }}>{tb.label}</span>
                    </TableCell>
                    <TableCell>{r.guest_name} <span className="text-xs text-[var(--muted)]">×{r.guest_count}</span></TableCell>
                    <TableCell>{r.supervisor_name}</TableCell>
                    <TableCell className="text-[var(--ink-2)]">{r.shift.split("(")[0].trim()}</TableCell>
                    <TableCell className="text-right">
                      <Link to={`/inspections/${r.id}`}>
                        <Button size="sm" variant="outline" className="border-[var(--border-warm)]"><FileText className="w-3.5 h-3.5 mr-1" /> Open</Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
